import axios from 'axios';
import { encryptEnvelope, decryptEnvelope, isEnvelope, ENC_HEADERS } from '../lib/cryptoEnvelope';

// Dynamic URL: uses REACT_APP_API_URL env var, or auto-detects from browser
// Local:      BASE_URL = http://localhost:5051, API_URL = http://localhost:5051/api
// Production: BASE_URL & API_URL = https://admin.vehiclesetu.cloud/api (all through nginx /api proxy)
const getUrls = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    if (envUrl.endsWith('/api')) {
      return { base: envUrl, api: envUrl };
    }
    return { base: envUrl, api: `${envUrl}/api` };
  }
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { base: 'http://localhost:5051', api: 'http://localhost:5051/api' };
  }
  // Production: everything goes through /api path (nginx proxies /api to Node)
  const apiUrl = `${protocol}//${hostname}/api`;
  return { base: apiUrl, api: apiUrl };
};

const { base: BASE_URL, api: API_URL } = getUrls();

// Exported so other modules (e.g. PromotionBanners image preview) can resolve
// server-relative upload paths to the same host axios is talking to.
export { BASE_URL, API_URL };

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available and handle FormData
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Remove Content-Type header for FormData to let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('admin');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Sensitive endpoints (auth) are sent through an AES-GCM + HMAC envelope.
// `encPost` wraps the body, awaits the (possibly encrypted) response, and
// returns a shape compatible with the original axios response so existing
// callers reading `res.data` don't need to change.
// Turn a raw response body into a usable object. These requests disable axios'
// JSON parsing (the body may be an envelope we have to decrypt first), so the
// body arrives as a string and has to be parsed here either way.
async function decodeBody(res) {
  let body = res.data;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { return res.data; }   // not JSON — hand back as-is
  }
  if (res.headers && res.headers['x-enc'] === '1' && isEnvelope(body)) {
    return decryptEnvelope(body);
  }
  return body;
}

async function encPost(url, data) {
  const envelope = await encryptEnvelope(data);
  try {
    const res = await api.post(url, envelope, {
      headers: ENC_HEADERS,
      transformResponse: [(raw) => raw],  // skip axios JSON.parse — we may need to decrypt first
    });
    return { ...res, data: await decodeBody(res) };
  } catch (err) {
    // Error bodies need the same treatment. Without this, callers reading
    // `err.response.data.error` get undefined — the string (often an encrypted
    // envelope) was never parsed — and every failure shows a generic message
    // instead of the reason the server actually gave.
    if (err.response) {
      try { err.response.data = await decodeBody(err.response); } catch { /* leave the raw body */ }
    }
    throw err;
  }
}

// Unwrap a response that may be an encrypted envelope. Routes mounted with
// `encrypted({ response: 'always' })` answer with an envelope and set X-Enc: 1.
async function unwrap(res) {
  return { ...res, data: await decodeBody(res) };
}

// GET / DELETE counterparts to `encPost`. These carry no request body, so the
// X-Enc header only signals "encrypt your response"; the server has nothing to
// decrypt. Used for endpoints returning personal data.
async function encGet(url, config = {}) {
  try {
    const res = await api.get(url, {
      ...config,
      headers: { ...(config.headers || {}), ...ENC_HEADERS },
      transformResponse: [(raw) => raw],
    });
    return unwrap(res);
  } catch (err) {
    if (err.response) {
      try { err.response.data = await decodeBody(err.response); } catch { /* leave the raw body */ }
    }
    throw err;
  }
}

async function encDelete(url, config = {}) {
  try {
    const res = await api.delete(url, {
      ...config,
      headers: { ...(config.headers || {}), ...ENC_HEADERS },
      transformResponse: [(raw) => raw],
    });
    return unwrap(res);
  } catch (err) {
    if (err.response) {
      try { err.response.data = await decodeBody(err.response); } catch { /* leave the raw body */ }
    }
    throw err;
  }
}

// Auth API calls
export const authAPI = {
  login: (data) => encPost('/auth/login', data),
  forgotPassword: (data) => encPost('/auth/forgot-password', data),
  resetPassword: (token, data) => encPost(`/auth/reset-password/${token}`, data),
  // Signed-in change; the interceptor adds the bearer token.
  changePassword: (data) => encPost('/auth/change-password', data),
  getMe: () => api.get('/auth/me')
};

// Car Brand API calls
export const carBrandAPI = {
  getAll: (params) => api.get('/car-brands', { params }),
  getById: (id) => api.get(`/car-brands/${id}`),
  create: (data) => api.post('/car-brands', data),
  update: (id, data) => api.put(`/car-brands/${id}`, data),
  delete: (id) => api.delete(`/car-brands/${id}`),
  sync: () => api.post('/car-brands/sync'),
  preview: () => api.get('/car-brands/preview'),
  syncSingle: (data) => api.post('/car-brands/sync-single', data)
};

// Car Model API calls
export const carModelAPI = {
  getAll: (params) => api.get('/car-models', { params }),
  getById: (id) => api.get(`/car-models/${id}`),
  create: (data) => api.post('/car-models', data),
  update: (id, data) => api.put(`/car-models/${id}`, data),
  delete: (id) => api.delete(`/car-models/${id}`),
  getBrands: () => api.get('/car-models/brands'),
  sync: (data) => api.post('/car-models/sync', data),
  preview: (params) => api.get('/car-models/preview', { params }),
  syncSingle: (data) => api.post('/car-models/sync-single', data)
};

// Car Variant API calls
export const carVariantAPI = {
  getAll: (params) => api.get('/car-variants', { params }),
  getById: (id) => api.get(`/car-variants/${id}`),
  create: (data) => api.post('/car-variants', data),
  update: (id, data) => api.put(`/car-variants/${id}`, data),
  delete: (id) => api.delete(`/car-variants/${id}`),
  getBrands: () => api.get('/car-variants/brands'),
  getModels: (params) => api.get('/car-variants/models', { params }),
  sync: (data) => api.post('/car-variants/sync', data),
  preview: (params) => api.get('/car-variants/preview', { params }),
  syncSingle: (data) => api.post('/car-variants/sync-single', data)
};

// Bike Brand API calls
export const bikeBrandAPI = {
  getAll: (params) => api.get('/bike-brands', { params }),
  getById: (id) => api.get(`/bike-brands/${id}`),
  create: (data) => api.post('/bike-brands', data),
  update: (id, data) => api.put(`/bike-brands/${id}`, data),
  delete: (id) => api.delete(`/bike-brands/${id}`),
  sync: () => api.post('/bike-brands/sync'),
  preview: () => api.get('/bike-brands/preview'),
  syncSingle: (data) => api.post('/bike-brands/sync-single', data)
};

// Bike Model API calls
export const bikeModelAPI = {
  getAll: (params) => api.get('/bike-models', { params }),
  getById: (id) => api.get(`/bike-models/${id}`),
  create: (data) => api.post('/bike-models', data),
  update: (id, data) => api.put(`/bike-models/${id}`, data),
  delete: (id) => api.delete(`/bike-models/${id}`),
  getBrands: () => api.get('/bike-models/brands'),
  sync: (data) => api.post('/bike-models/sync', data),
  preview: (params) => api.get('/bike-models/preview', { params }),
  syncSingle: (data) => api.post('/bike-models/sync-single', data)
};

// Bike Variant API calls
export const bikeVariantAPI = {
  getAll: (params) => api.get('/bike-variants', { params }),
  getById: (id) => api.get(`/bike-variants/${id}`),
  create: (data) => api.post('/bike-variants', data),
  update: (id, data) => api.put(`/bike-variants/${id}`, data),
  delete: (id) => api.delete(`/bike-variants/${id}`),
  getBrands: () => api.get('/bike-variants/brands'),
  getModels: (params) => api.get('/bike-variants/models', { params }),
  sync: (data) => api.post('/bike-variants/sync', data),
  preview: (params) => api.get('/bike-variants/preview', { params }),
  syncSingle: (data) => api.post('/bike-variants/sync-single', data)
};

// Bike Service Center API calls
export const bikeServiceCenterAPI = {
  getAll: (params) => api.get('/bike-service-centers', { params }),
  getById: (id) => api.get(`/bike-service-centers/${id}`),
  delete: (id) => api.delete(`/bike-service-centers/${id}`),
  getCities: () => api.get('/bike-service-centers/cities'),
  getBrands: () => api.get('/bike-service-centers/brands'),
  getBikeBrands: () => api.get('/bike-service-centers/bike-brands'),
  preview: (params) => api.get('/bike-service-centers/preview', { params }),
  syncSingle: (data) => api.post('/bike-service-centers/sync-single', data),
  syncAll: (mode = 'smart') => api.post('/bike-service-centers/sync-all', { mode }),
  getSyncProgress: () => api.get('/bike-service-centers/sync-progress'),
  resetSyncProgress: () => api.post('/bike-service-centers/sync-reset')
};

// Bike Dealer API calls
export const bikeDealerAPI = {
  getAll: (params) => api.get('/bike-dealers', { params }),
  getById: (id) => api.get(`/bike-dealers/${id}`),
  delete: (id) => api.delete(`/bike-dealers/${id}`),
  getCities: () => api.get('/bike-dealers/cities'),
  getBrands: () => api.get('/bike-dealers/brands'),
  getBikeBrands: () => api.get('/bike-dealers/bike-brands'),
  preview: (params) => api.get('/bike-dealers/preview', { params }),
  syncSingle: (data) => api.post('/bike-dealers/sync-single', data),
  syncAll: (mode = 'smart') => api.post('/bike-dealers/sync-all', { mode }),
  getSyncProgress: () => api.get('/bike-dealers/sync-progress'),
  resetSyncProgress: () => api.post('/bike-dealers/sync-reset')
};

// Car Service Center API calls
export const carServiceCenterAPI = {
  getAll: (params) => api.get('/car-service-centers', { params }),
  getById: (id) => api.get(`/car-service-centers/${id}`),
  delete: (id) => api.delete(`/car-service-centers/${id}`),
  getCities: () => api.get('/car-service-centers/cities'),
  getBrands: () => api.get('/car-service-centers/brands'),
  getCarBrands: () => api.get('/car-service-centers/car-brands'),
  sync: (data) => api.post('/car-service-centers/sync', data),
  syncAll: (mode = 'smart') => api.post('/car-service-centers/sync-all', { mode }),
  getSyncProgress: () => api.get('/car-service-centers/sync-progress'),
  resetSyncProgress: () => api.post('/car-service-centers/sync-reset'),
  preview: (params) => api.get('/car-service-centers/preview', { params }),
  syncSingle: (data) => api.post('/car-service-centers/sync-single', data)
};

// Car Dealer API calls
export const carDealerAPI = {
  getAll: (params) => api.get('/car-dealers', { params }),
  getById: (id) => api.get(`/car-dealers/${id}`),
  delete: (id) => api.delete(`/car-dealers/${id}`),
  getCities: () => api.get('/car-dealers/cities'),
  getBrands: () => api.get('/car-dealers/brands'),
  getCarBrands: () => api.get('/car-dealers/car-brands'),
  preview: (params) => api.get('/car-dealers/preview', { params }),
  syncSingle: (data) => api.post('/car-dealers/sync-single', data),
  syncAll: (mode = 'smart') => api.post('/car-dealers/sync-all', { mode }),
  getSyncProgress: () => api.get('/car-dealers/sync-progress'),
  resetSyncProgress: () => api.post('/car-dealers/sync-reset')
};

// City API calls
export const cityAPI = {
  getAll: (params) => api.get('/cities', { params }),
  getPopular: () => api.get('/cities/popular'),
  getById: (id) => api.get(`/cities/${id}`),
  update: (id, data) => api.put(`/cities/${id}`, data),
};

// Driving School API calls
export const drivingSchoolAPI = {
  getAll: (params) => api.get('/driving-schools', { params }),
  getById: (id) => api.get(`/driving-schools/${id}`),
  create: (data) => api.post('/driving-schools', data),
  update: (id, data) => api.put(`/driving-schools/${id}`, data),
  delete: (id) => api.delete(`/driving-schools/${id}`),
  getCities: () => api.get('/driving-schools/cities'),
  syncAll: (mode = 'smart') => api.post('/driving-schools/sync-all', { mode }),
  getSyncProgress: () => api.get('/driving-schools/sync-progress'),
  resetSyncProgress: () => api.post('/driving-schools/sync-reset')
};

// RTO Office API calls
export const rtoAPI = {
  getAll: (params) => api.get('/rto-offices', { params }),
  getById: (id) => api.get(`/rto-offices/${id}`),
  create: (data) => api.post('/rto-offices', data),
  update: (id, data) => api.put(`/rto-offices/${id}`, data),
  delete: (id) => api.delete(`/rto-offices/${id}`),
  getStates: () => api.get('/rto-offices/states'),
  getDistricts: (params) => api.get('/rto-offices/districts', { params }),
  syncAll: (mode = 'smart') => api.post('/rto-offices/sync-all', { mode })
};

// Question Bank API calls
export const questionBankAPI = {
  getStates: () => api.get('/question-bank/states'),
  getLocales: (params) => api.get('/question-bank/locales', { params }),
  getQuestions: (params) => api.get('/question-bank/questions', { params }),
  getQuestionById: (id) => api.get(`/question-bank/questions/${id}`),
  createQuestion: (data) => api.post('/question-bank/questions', data),
  updateQuestion: (id, data) => api.put(`/question-bank/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/question-bank/questions/${id}`),
  syncStates: () => api.post('/question-bank/sync-states'),
  syncQuestions: (stateId, locale) => api.post('/question-bank/sync-questions', { stateId, locale }),
  syncAll: (mode = 'smart') => api.post('/question-bank/sync-all', { mode }),
  getSyncProgress: () => api.get('/question-bank/sync-progress'),
  resetSyncProgress: () => api.post('/question-bank/sync-reset')
};

// Practice Questions API calls
export const practiceQuestionAPI = {
  getStates: () => api.get('/practice-questions/states'),
  getLocales: (params) => api.get('/practice-questions/locales', { params }),
  getQuestions: (params) => api.get('/practice-questions/questions', { params }),
  getQuestionById: (id) => api.get(`/practice-questions/questions/${id}`),
  createQuestion: (data) => api.post('/practice-questions/questions', data),
  updateQuestion: (id, data) => api.put(`/practice-questions/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/practice-questions/questions/${id}`),
  syncQuestions: (stateId, locale) => api.post('/practice-questions/sync-questions', { stateId, locale }),
  syncAll: (mode = 'smart') => api.post('/practice-questions/sync-all', { mode }),
  getSyncProgress: () => api.get('/practice-questions/sync-progress'),
  resetSyncProgress: () => api.post('/practice-questions/sync-reset')
};

// Exam Questions API calls
export const examQuestionAPI = {
  getStates: () => api.get('/exam-questions/states'),
  getLocales: (params) => api.get('/exam-questions/locales', { params }),
  getQuestions: (params) => api.get('/exam-questions/questions', { params }),
  getQuestionById: (id) => api.get(`/exam-questions/questions/${id}`),
  createQuestion: (data) => api.post('/exam-questions/questions', data),
  updateQuestion: (id, data) => api.put(`/exam-questions/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/exam-questions/questions/${id}`),
  syncQuestions: (stateId, locale) => api.post('/exam-questions/sync-questions', { stateId, locale }),
  syncAll: (mode = 'smart') => api.post('/exam-questions/sync-all', { mode }),
  getSyncProgress: () => api.get('/exam-questions/sync-progress'),
  resetSyncProgress: () => api.post('/exam-questions/sync-reset')
};

// Traffic Signals API calls
export const trafficSignalAPI = {
  getAll: (params) => api.get('/traffic-signals', { params }),
  getById: (id) => api.get(`/traffic-signals/${id}`),
  getLocales: () => api.get('/traffic-signals/locales'),
  getAvailableLocales: () => api.get('/traffic-signals/available-locales'),
  getCategories: (params) => api.get('/traffic-signals/categories', { params }),
  create: (data) => api.post('/traffic-signals', data),
  update: (id, data) => api.put(`/traffic-signals/${id}`, data),
  delete: (id) => api.delete(`/traffic-signals/${id}`),
  sync: (locale) => api.post('/traffic-signals/sync', { locale }),
  syncAll: () => api.post('/traffic-signals/sync-all')
};

// Ad Platform API calls
export const adPlatformAPI = {
  getAll: (params) => api.get('/ad-platforms', { params }),
  getById: (id) => api.get(`/ad-platforms/${id}`),
  create: (data) => api.post('/ad-platforms', data),
  update: (id, data) => api.put(`/ad-platforms/${id}`, data),
  delete: (id) => api.delete(`/ad-platforms/${id}`),
  getTypes: () => api.get('/ad-platforms/types'),
  getStatuses: () => api.get('/ad-platforms/statuses')
};

// Ad Placement API calls
// Application API — top-level container that groups ad platforms.
export const applicationAPI = {
  getAll: (params) => api.get('/applications', { params }),
  getById: (id) => api.get(`/applications/${id}`),
  create: (data) => api.post('/applications', data),
  update: (id, data) => api.put(`/applications/${id}`, data),
  delete: (id) => api.delete(`/applications/${id}`),
  getStores: () => api.get('/applications/stores'),
  getStatuses: () => api.get('/applications/statuses'),
};

export const adPlacementAPI = {
  getAll: (params) => api.get('/ad-placements', { params }),
  getById: (id) => api.get(`/ad-placements/${id}`),
  create: (data) => api.post('/ad-placements', data),
  update: (id, data) => api.put(`/ad-placements/${id}`, data),
  delete: (id) => api.delete(`/ad-placements/${id}`),
  getTypes: () => api.get('/ad-placements/types'),
  getStatuses: () => api.get('/ad-placements/statuses'),
  getScreenNames: () => api.get('/ad-placements/screen-names')
};

// Ad Analytics API calls
export const adAnalyticsAPI = {
  getOverview: (params) => api.get('/ad-analytics/overview', { params }),
  getByPlatform: (params) => api.get('/ad-analytics/by-platform', { params }),
  getByPlacement: (params) => api.get('/ad-analytics/by-placement', { params }),
  getTrend: (params) => api.get('/ad-analytics/trend', { params }),
  getErrors: (params) => api.get('/ad-analytics/errors', { params }),
  getRecentEvents: (params) => api.get('/ad-analytics/events', { params })
};

// Upload API calls
export const uploadAPI = {
  uploadPlatformImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type manually - axios will set it with the correct boundary
    return api.post('/upload/platform-image', formData);
  },
  deletePlatformImage: (filename) => api.delete(`/upload/platform-image/${filename}`),
  uploadQuestionImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type manually - axios will set it with the correct boundary
    return api.post('/upload/question-image', formData);
  },
  deleteQuestionImage: (filename) => api.delete(`/upload/question-image/${filename}`),
  uploadTipImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/tip-image', formData);
  },
  deleteTipImage: (filename) => api.delete(`/upload/tip-image/${filename}`),
  uploadBannerImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/banner-image', formData);
  },
  deleteBannerImage: (filename) => api.delete(`/upload/banner-image/${filename}`),
  uploadPaymentDialogImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/payment-dialog-image', formData);
  },
  deletePaymentDialogImage: (filename) => api.delete(`/upload/payment-dialog-image/${filename}`),
  uploadCityImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/city-image', formData);
  },
  deleteCityImage: (filename) => api.delete(`/upload/city-image/${filename}`),
  uploadAppIcon: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/app-icon', formData);
  },
  deleteAppIcon: (filename) => api.delete(`/upload/app-icon/${filename}`)
};

// Screen Config API calls (Lottie JSON for screens)
export const screenConfigAPI = {
  getAll: (params) => api.get('/screen-configs', { params }),
  getById: (id) => api.get(`/screen-configs/${id}`),
  getByScreenId: (screenId) => api.get(`/screen-configs/by-screen-id/${screenId}`),
  create: (data) => api.post('/screen-configs', data),
  update: (id, data) => api.put(`/screen-configs/${id}`, data),
  delete: (id) => api.delete(`/screen-configs/${id}`),
  toggleStatus: (id) => api.patch(`/screen-configs/${id}/toggle`)
};

// Valuation API calls
export const valuationAPI = {
  getCarManufacturers: () => api.get('/valuation/car/manufacturers'),
  getBikeManufacturers: () => api.get('/valuation/bike/manufacturers'),
  getYears: (params) => api.get('/valuation/years', { params }),
  getCarModels: (params) => api.get('/valuation/car/models', { params }),
  getBikeModels: (params) => api.get('/valuation/bike/models', { params }),
  getCarVariants: (params) => api.get('/valuation/car/variants', { params }),
  getBikeVariants: (params) => api.get('/valuation/bike/variants', { params }),
  calculateCarValuation: (data) => api.post('/valuation/car/calculate', data),
  calculateBikeValuation: (data) => api.post('/valuation/bike/calculate', data),
  getCities: () => api.get('/valuation/cities')
};

// Content Module API calls (HTML content pages for mobile app)
export const contentModuleAPI = {
  getAll: (params) => api.get('/content-modules', { params }),
  getById: (id) => api.get(`/content-modules/${id}`),
  create: (data) => api.post('/content-modules', data),
  update: (id, data) => api.put(`/content-modules/${id}`, data),
  delete: (id) => api.delete(`/content-modules/${id}`),
  toggleStatus: (id) => api.patch(`/content-modules/${id}/toggle`),
  reorder: (orders) => api.post('/content-modules/reorder', { orders })
};

// External Links API calls (sidebar webview links for mobile app)
export const externalLinkAPI = {
  getAll: (params) => api.get('/external-links', { params }),
  getById: (id) => api.get(`/external-links/${id}`),
  create: (data) => api.post('/external-links', data),
  update: (id, data) => api.put(`/external-links/${id}`, data),
  delete: (id) => api.delete(`/external-links/${id}`),
  toggleStatus: (id) => api.patch(`/external-links/${id}/toggle`),
  reorder: (orders) => api.post('/external-links/reorder', { orders })
};

// Promotion Banners API — image/lottie banners for mobile app screens
export const promotionBannerAPI = {
  getAll: (params) => api.get('/promotion-banners', { params }),
  getById: (id) => api.get(`/promotion-banners/${id}`),
  create: (data) => api.post('/promotion-banners', data),
  update: (id, data) => api.put(`/promotion-banners/${id}`, data),
  delete: (id) => api.delete(`/promotion-banners/${id}`),
};

// Contact Submissions API — read-only list of public contact form submissions.
export const contactSubmissionAPI = {
  getAll: (params) => api.get('/contact-submissions', { params }),
};

// Payment Dialogs API — image + button + description cards shown in the mobile
// app's payment flow.
export const paymentDialogAPI = {
  getAll: (params) => api.get('/payment-dialogs', { params }),
  getById: (id) => api.get(`/payment-dialogs/${id}`),
  create: (data) => api.post('/payment-dialogs', data),
  update: (id, data) => api.put(`/payment-dialogs/${id}`, data),
  delete: (id) => api.delete(`/payment-dialogs/${id}`),
};

// Payment Keys API — per-app payment-gateway credentials. `getAll` returns the
// salt masked; `getById` is the only endpoint that returns the raw salt.
export const paymentKeyAPI = {
  getAll: (params) => api.get('/payment-keys', { params }),
  getById: (id) => api.get(`/payment-keys/${id}`),
  create: (data) => api.post('/payment-keys', data),
  update: (id, data) => api.put(`/payment-keys/${id}`, data),
  delete: (id) => api.delete(`/payment-keys/${id}`),
};

// Vehicle Info API — the shared RC cache (read + delete only). Owner names and
// addresses travel on these routes, so every call is enveloped.
export const vehicleInfoAPI = {
  getAll: (params) => encGet('/vehicle-info', { params }),
  getById: (id) => encGet(`/vehicle-info/${id}`),
  delete: (id) => encDelete(`/vehicle-info/${id}`),
};

// FAQ API calls
export const faqAPI = {
  getAll: (params) => api.get('/faqs', { params }),
  getById: (id) => api.get(`/faqs/${id}`),
  create: (data) => api.post('/faqs', data),
  update: (id, data) => api.put(`/faqs/${id}`, data),
  delete: (id) => api.delete(`/faqs/${id}`),
  togglePublish: (id) => api.patch(`/faqs/${id}/toggle`),
  // Categories
  getCategories: () => api.get('/faqs/categories'),
  createCategory: (data) => api.post('/faqs/categories', data),
  updateCategory: (id, data) => api.put(`/faqs/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/faqs/categories/${id}`),
};

// Testimonial API calls
export const testimonialAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
  togglePublish: (id) => api.patch(`/testimonials/${id}/toggle`)
};

// Blog API calls
export const blogAPI = {
  getAll: (params) => api.get('/blogs', { params }),
  getById: (id) => api.get(`/blogs/${id}`),
  create: (data) => api.post('/blogs', data),
  update: (id, data) => api.put(`/blogs/${id}`, data),
  delete: (id) => api.delete(`/blogs/${id}`),
  togglePublish: (id) => api.patch(`/blogs/${id}/toggle`),
  getCategories: () => api.get('/blogs/categories'),
  createCategory: (data) => api.post('/blogs/categories', data),
  deleteCategory: (id) => api.delete(`/blogs/categories/${id}`)
};

// Traffic Rule API calls
export const trafficRuleAPI = {
  getAll: (params) => api.get('/traffic-rules', { params }),
  getStates: () => api.get('/traffic-rules/states'),
  getById: (id) => api.get(`/traffic-rules/${id}`),
  create: (data) => api.post('/traffic-rules', data),
  update: (id, data) => api.put(`/traffic-rules/${id}`, data),
  toggleActive: (id) => api.patch(`/traffic-rules/${id}/toggle`),
  delete: (id) => api.delete(`/traffic-rules/${id}`),
};

// Driving Tip API calls
export const drivingTipAPI = {
  getAll: () => api.get('/driving-tips'),
  getById: (id) => api.get(`/driving-tips/${id}`),
  create: (data) => api.post('/driving-tips', data),
  update: (id, data) => api.put(`/driving-tips/${id}`, data),
  toggleActive: (id) => api.patch(`/driving-tips/${id}/toggle`),
  delete: (id) => api.delete(`/driving-tips/${id}`),
};

// Exclusive Service API calls (external service links on homepage)
export const exclusiveServiceAPI = {
  getAll: () => api.get('/exclusive-services'),
  getById: (id) => api.get(`/exclusive-services/${id}`),
  create: (data) => api.post('/exclusive-services', data),
  update: (id, data) => api.put(`/exclusive-services/${id}`, data),
  toggleActive: (id) => api.patch(`/exclusive-services/${id}/toggle`),
  delete: (id) => api.delete(`/exclusive-services/${id}`),
};

// Social Link API calls (footer social media icons)
export const socialLinkAPI = {
  getAll: () => api.get('/social-links'),
  getById: (id) => api.get(`/social-links/${id}`),
  create: (data) => api.post('/social-links', data),
  update: (id, data) => api.put(`/social-links/${id}`, data),
  toggleActive: (id) => api.patch(`/social-links/${id}/toggle`),
  delete: (id) => api.delete(`/social-links/${id}`),
};

// Insurance Partner Link API calls (third-party insurance affiliate URLs - website only)
export const insurancePartnerLinkAPI = {
  getAll: () => api.get('/insurance-partner-links'),
  getById: (id) => api.get(`/insurance-partner-links/${id}`),
  create: (data) => api.post('/insurance-partner-links', data),
  update: (id, data) => api.put(`/insurance-partner-links/${id}`, data),
  toggleActive: (id) => api.patch(`/insurance-partner-links/${id}/toggle`),
  delete: (id) => api.delete(`/insurance-partner-links/${id}`),
};

export default api;
